import { useEffect, useRef, useState } from "react";
import { useRelayEnvironment } from "react-relay";
import { fetchQuery, graphql } from "relay-runtime";
import { Asset, BalanceAccount } from "rujira.js";
import { Id } from "../node";
import { useTransferBalancesQuery } from "./__generated__/useTransferBalancesQuery.graphql";
import {
  nextTransferBalanceState,
  type TransferBalanceEntry,
  type TransferBalanceState,
} from "./transferBalanceState";

const query = graphql`
  query useTransferBalancesQuery(
    $id: ID!
    $addresses: [Address!]!
    $after: String
  ) {
    node(id: $id) {
      ... on Account {
        balancesV2(first: 100, after: $after, addresses: $addresses) @catch {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              accounts {
                address
                balance
                valueUsd
                asset {
                  asset
                  type
                  chain
                  price {
                    current
                  }
                  metadata {
                    symbol
                    decimals
                  }
                  variants {
                    native {
                      denom
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export type { TransferBalanceEntry };

export const useTransferBalances = (addresses: string[], asset: Asset) => {
  const environment = useRelayEnvironment();
  const addressKey = [...new Set(addresses)].sort().join(",");
  const key = `${asset.asset}:${addressKey}`;
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<TransferBalanceState>();
  // Read through a ref so a new object for the same asset (same `key`) does not
  // restart the fetch; `key` already changes whenever the asset really does.
  const assetRef = useRef(asset);
  assetRef.current = asset;

  useEffect(() => {
    if (!addressKey) {
      setState({ key, entries: {} });
      return;
    }
    setState(undefined);
    let active = true;
    const requestedAddresses = addressKey.split(",");
    let subscription: { unsubscribe: () => void } | undefined;

    const finish = (accounts?: BalanceAccount[]) => {
      if (!active) return;
      setState((previous) =>
        nextTransferBalanceState({
          previous,
          key,
          addresses: requestedAddresses,
          asset: assetRef.current,
          accounts,
        })
      );
    };

    const loadPage = (after?: string, previous: BalanceAccount[] = []) => {
      subscription?.unsubscribe();
      subscription = fetchQuery<useTransferBalancesQuery>(
        environment,
        query,
        {
          id: Id.account({ address: requestedAddresses[0] }),
          addresses: requestedAddresses.slice(1),
          after,
        },
        { fetchPolicy: "network-only" }
      ).subscribe({
        next: (data) => {
          if (!active) return;
          const balances = data.node?.balancesV2;
          if (!balances?.ok) return finish();
          const accounts = [
            ...previous,
            ...(balances.value.edges?.flatMap(
              (edge) => edge?.node?.accounts || []
            ) || []),
          ];
          const cursor = balances.value.pageInfo.endCursor;
          if (balances.value.pageInfo.hasNextPage) {
            if (!cursor || cursor === after) return finish();
            loadPage(cursor, accounts);
          } else {
            finish(accounts);
          }
        },
        error: () => finish(),
      });
    };

    const refresh = () => loadPage();
    refresh();
    const timer = setInterval(refresh, 30_000);
    return () => {
      active = false;
      clearInterval(timer);
      subscription?.unsubscribe();
    };
  }, [addressKey, attempt, environment, key]);

  return {
    entries: state?.key === key ? state.entries : {},
    loading: Boolean(addressKey) && state?.key !== key,
    retry: () => setAttempt((value) => value + 1),
  };
};
